import { resolveSpaceWebGatewayOrigin } from "./web-url";

const SPACE_ID_HEX_REGEX = /^[0-9a-f]{64}$/i;
const INVALID_SPACE_TARGET_MESSAGE =
  "Space target must be a 64-character Space ID or an OpenMeld Space URL.";

export type ParsedSpaceTarget = {
  baseUrl?: string;
  focusInteractionId?: string;
  focusSignalId?: string;
  focusThreadRootSignalId?: string;
  profileId?: string;
  spaceId: string;
};

export function parseSpaceTarget(
  value: string,
  options?: { baseUrl?: string }
): ParsedSpaceTarget {
  const explicitBaseUrl = normalizeOptionalString(options?.baseUrl);
  const target = parseSpaceTargetValue(value, !explicitBaseUrl);
  return {
    ...target,
    ...(explicitBaseUrl ? { baseUrl: explicitBaseUrl } : {}),
  };
}

function parseSpaceTargetValue(
  value: string,
  deriveBaseUrl: boolean
): ParsedSpaceTarget {
  const raw = normalizeOptionalString(value);
  if (!raw) {
    throw new Error(INVALID_SPACE_TARGET_MESSAGE);
  }
  if (SPACE_ID_HEX_REGEX.test(raw)) {
    return { spaceId: raw };
  }

  const isPathOnly = raw.startsWith("/");
  let url: URL;
  let segments: string[];
  try {
    url = isPathOnly ? new URL(raw, "https://openmeld.ai") : new URL(raw);
    segments = url.pathname
      .split("/")
      .map((segment) => decodeURIComponent(segment.trim()))
      .filter((segment) => segment.length > 0);
  } catch {
    throw new Error(INVALID_SPACE_TARGET_MESSAGE);
  }
  const spacesIndex = segments.indexOf("spaces");
  const spaceId = spacesIndex >= 0 ? segments[spacesIndex + 1] : undefined;
  if (!(spaceId && SPACE_ID_HEX_REGEX.test(spaceId))) {
    throw new Error(
      "Space URL must include /spaces/<space-id> with a full 64-character Space ID. Organization-scoped /o/<org-slug>/spaces/<space-id> and legacy /app/spaces/<space-id> inputs are also accepted."
    );
  }
  const profileId = normalizeOptionalString(url.searchParams.get("profileId"));
  const focusInteractionId = normalizeOptionalString(
    url.searchParams.get("focusInteractionId")
  );
  const focusSignalId =
    normalizeOptionalString(url.searchParams.get("focusSignalId")) ??
    normalizeOptionalString(url.searchParams.get("signalId"));
  const focusThreadRootSignalId =
    normalizeOptionalString(url.searchParams.get("focusThreadRootSignalId")) ??
    normalizeOptionalString(url.searchParams.get("threadRootSignalId"));
  return {
    spaceId,
    ...(profileId ? { profileId } : {}),
    ...(focusInteractionId ? { focusInteractionId } : {}),
    ...(focusSignalId ? { focusSignalId } : {}),
    ...(focusThreadRootSignalId ? { focusThreadRootSignalId } : {}),
    ...(isPathOnly || !deriveBaseUrl
      ? {}
      : { baseUrl: resolveSpaceWebGatewayOrigin(url) }),
  };
}

export function resolveSpaceEntryCommandTarget(input: {
  baseUrl?: string;
  profile?: string;
  space: string;
}): {
  baseUrl?: string;
  focusInteractionId?: string;
  profile?: string;
  signalId?: string;
  space: string;
  threadRootSignalId?: string;
} {
  const target = parseSpaceTarget(input.space, { baseUrl: input.baseUrl });
  const explicitProfile = normalizeOptionalString(input.profile);
  const profile = explicitProfile ?? target.profileId;
  return {
    space: target.spaceId,
    ...(target.baseUrl ? { baseUrl: target.baseUrl } : {}),
    ...(target.focusInteractionId
      ? { focusInteractionId: target.focusInteractionId }
      : {}),
    ...(target.focusSignalId ? { signalId: target.focusSignalId } : {}),
    ...(target.focusThreadRootSignalId
      ? { threadRootSignalId: target.focusThreadRootSignalId }
      : {}),
    ...(profile ? { profile } : {}),
  };
}

export function resolveSpaceReadCommandTarget(input: {
  baseUrl?: string;
  profile?: string;
  space: string;
}): {
  baseUrl?: string;
  profile?: string;
  space: string;
} {
  const target = parseSpaceTarget(input.space, { baseUrl: input.baseUrl });
  const explicitProfile = normalizeOptionalString(input.profile);
  const profile = explicitProfile ?? target.profileId;
  return {
    space: target.spaceId,
    ...(target.baseUrl ? { baseUrl: target.baseUrl } : {}),
    ...(profile ? { profile } : {}),
  };
}

function normalizeOptionalString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}
